import { useEffect, useState, type ReactElement } from 'react';
import { LinktreeLogo } from './LinktreeLogo';
import {
  AUTH_SCREEN_READY_EVENT,
  AUTH_SCREEN_READY_MAX_WAIT_MS,
  isAuthScreenReady,
} from '../utils/useAuthScreenReady';

// Keep this critical-path shell independent of i18n initialization. The label
// deliberately matches the parser-rendered shell that React replaces. The
// mid-session pending-gate fallback also accepts this untranslated copy for its
// bounded loading state.
const LOADING_SHELL_LABEL = 'Loading';
const LOADING_SHELL_HEADING = 'Just a minute';
const LOADING_SHELL_SUBHEADING = "We're getting your Linktree ready.";

export const LoadingShell = (): ReactElement => {
  return (
    <div
      id="auth-loading-shell"
      className="auth-loading-shell"
      role="status"
      aria-live="polite"
      aria-label={LOADING_SHELL_LABEL}
    >
      <div className="auth-loading-shell__logo" aria-hidden="true">
        <LinktreeLogo title="" description="" />
      </div>
      <div className="auth-loading-shell__content-column">
        <div className="auth-loading-shell__content">
          <h1 className="auth-loading-shell__heading">
            {LOADING_SHELL_HEADING}
          </h1>
          <p className="auth-loading-shell__subheading">
            {LOADING_SHELL_SUBHEADING}
          </p>
          <div className="auth-loading-shell__form" aria-hidden="true">
            <span className="auth-loading-shell__skeleton auth-loading-shell__skeleton--input" />
            <span className="auth-loading-shell__skeleton auth-loading-shell__skeleton--primary" />
            <span className="auth-loading-shell__divider" />
            <span className="auth-loading-shell__skeleton auth-loading-shell__skeleton--social" />
            <span className="auth-loading-shell__skeleton auth-loading-shell__skeleton--social auth-loading-shell__skeleton--social-last" />
          </div>
        </div>
      </div>
      <div className="auth-loading-shell__banner" aria-hidden="true" />
    </div>
  );
};

export const AuthLoadingShell = (): ReactElement | null => {
  const [isScreenReady, setIsScreenReady] = useState(isAuthScreenReady);

  useEffect(() => {
    const handleAuthScreenReady = (): void => {
      setIsScreenReady(true);
    };

    if (isAuthScreenReady()) {
      handleAuthScreenReady();
      return undefined;
    }

    window.addEventListener(AUTH_SCREEN_READY_EVENT, handleAuthScreenReady, {
      once: true,
    });
    const maxWaitTimer = window.setTimeout(
      handleAuthScreenReady,
      AUTH_SCREEN_READY_MAX_WAIT_MS
    );

    return () => {
      window.clearTimeout(maxWaitTimer);
      window.removeEventListener(
        AUTH_SCREEN_READY_EVENT,
        handleAuthScreenReady
      );
    };
  }, []);

  return isScreenReady ? null : <LoadingShell />;
};
